import React, {useState, useEffect} from "react";
import {NavLink, useLocation} from "react-router-dom";
import useWindowDimensions from "../../../hooks/useWindowDimensions";
import { resetReduxState } from "../../../redux/store";
// import AscendLogo from "../../../assets/ACS-Images/Ascend-Logo.png";
import Cookies from "js-cookie";
import {
    LayoutDashboard,
    Package,
    ShoppingBag,
    FileText,
    Truck,
    Users,
    LogOut,
    Menu,
    FileSignature,
    FileInput,
    CalendarDays,
    Bot,
    MapPin,
    BarChart2,
    Warehouse,
    SlidersHorizontal,
    ClipboardList
} from "lucide-react";
import AOS from "aos";
import "aos/dist/aos.css";
import axios from "axios";

const navItems = [
    // {to: "/dashboard", icon: LayoutDashboard, text: "Dashboard"},
    // {to: "/dashboard/inventory", icon: Package, text: "Inventory"},
    {to: "/dashboard/orders", icon: ShoppingBag, text: "Orders"},
    {to: "/dashboard/shipments", icon: Truck, text: "Shipments"},
    {to: "/dashboard/appointments", icon: CalendarDays, text: "Appointments"},
    {to: "/dashboard/invoices", icon: FileText, text: "Invoices"},
    {to: "/dashboard/contracts", icon: FileSignature, text: "Contracts"},
    {to: "/dashboard/upload", icon: FileInput, text: "AI Document Reader"},
    {to: "/dashboard/manage-team", icon: Users, text: "Manage Team"},
    {to: "/dashboard/locations", icon: MapPin, text: "Locations"},
    {to: "/dashboard/resource-selection", icon: SlidersHorizontal, text: "Resource Selection"},
    {to: "/dashboard/transaction-audit", icon: ClipboardList, text: "Transaction Audit"},
];

const Sidebar = () => {
    const {width} = useWindowDimensions();
    const isMobile = width < 768;
    const [isCollapsed, setIsCollapsed] = useState(() => {
        const storedState = localStorage.getItem("isCollapsed");

        return storedState !== null ? storedState === "true" : true;
    });

    const [allowedMenus, setAllowedMenus] = useState([]);
    const location = useLocation();

    useEffect(() => {
        AOS.init({duration: 500});
    }, []);

    useEffect(() => {
        localStorage.setItem("isCollapsed", isCollapsed);
    }, [isCollapsed]);

    useEffect(() => {
        const storedMenus = localStorage.getItem("allowedMenus");
        if (storedMenus?.length > 2) {
            try {
                setAllowedMenus(JSON.parse(storedMenus));
            } catch (e) {
                console.warn("Failed to parse allowedMenus from localStorage:", e);
            }
        }

    }, []);

    const isActiveRoute = (path) => location.pathname === path;

    const navLinkStyles = (path) =>
        `flex items-center justify-start text-start ${
            isCollapsed ? "pl-2" : "pl-fluid-3"
        } pr-2 py-2 rounded-full transition-all duration-300 group ${
            isActiveRoute(path)
                ? "text-black dark:text-[#1A1C23] bg-acs-primary-green bg-opacity-90"
                : "text-white dark:text-gray-200 hover:bg-[#6DE1C2]/30 dark:hover:bg-acs-primary-green/20"
        }`;


    const handleNavClick = () => {
        if (isMobile && !isCollapsed) {
            setIsCollapsed(true);
            localStorage.setItem("isCollapsed", "true");
        }
    };

    const handleLogout = () => {
        const cookieDomain = process.env.REACT_APP_COOKIE_DOMAIN;
        const cookieAttrs = {path: "/"};
        if (cookieDomain) cookieAttrs.domain = cookieDomain;

        Cookies.remove("authToken", cookieAttrs);
        Cookies.remove("userData", cookieAttrs);
        Cookies.remove("officeCode", cookieAttrs);
        Cookies.remove("officeCodes", cookieAttrs);

        // 3) Clear storage (Redux cache & app state persisted in storage)
        resetReduxState();
        localStorage.removeItem("isCollapsed");
        localStorage.removeItem("allowedMenus");
        localStorage.removeItem("userPrivileges");
        localStorage.removeItem("menuTabStructure");
        localStorage.removeItem("organizationData");
        localStorage.removeItem("officeCode");
        localStorage.removeItem("organizationTypes")
        // localStorage.removeItem("theme");
        sessionStorage.clear();

        // 4) Optional: clear any axios auth header you might have set globally
        if (axios.defaults.headers.common.Authorization) {
            delete axios.defaults.headers.common.Authorization;
        }

        // 6) Hard navigate to login to ensure a clean shell
        window.location.replace("/login");
    };

    const visibleNavItems = navItems.filter((item) => {
        const parts = item.to.split("/");
        const routeKey = parts.length === 2 ? parts[1] : parts[2];

        // Singular/plural fix
        const normalizedKey = routeKey.endsWith("s")
            ? routeKey.slice(0, -1)
            : routeKey;
        return (
            allowedMenus.includes(routeKey) || allowedMenus.includes(normalizedKey)
        );
    });

    const orgTypes = JSON.parse(localStorage.getItem("organizationTypes")||"[]");
    const isCarrier = orgTypes.includes("carrier");

    // ── Locations visibility: requires the locations:VIEW privilege ──
    // A dedicated privilege "locations:VIEW" (menu name "locations") is
    // assigned only to Admin and Shipper roles — NOT Carrier.
    const canSeeLocations = allowedMenus.includes("locations");

    return (
        <div
            className={`fixed md:relative ${
                isCollapsed
                    ? "w-[clamp(3.5rem,5vw,5rem)]"
                    : "w-[clamp(12rem,15vw,16rem)]"
            } min-h-screen bg-acs-primary-navy dark:bg-acs-primary-navy-dark text-white 
          p-fluid-3 pt-[clamp(3.75rem,6vw,4.375rem)] flex flex-col 
          transition-all  ease-in-out 
          ${isMobile && isCollapsed ? "-translate-x-full" : "translate-x-0"} 
          md:translate-x-0 z-[1200] font-roboto`}
        >
            {/* Toggle Sidebar Button */}
            <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="mb-fluid-2  pt-6 p-fluid-1 hover:bg-white/10 rounded-full transition-all duration-300 flex items-center justify-center"
            >
                <Menu className="w-[1em] h-[1em] text-white"/>
            </button>

            <hr className="border-white/40 w-full mb-fluid-2"/>

            {/* Navigation Links */}
            <nav className="w-full flex-1 flex flex-col">
                <ul className="flex flex-col gap-fluid-2">
                    {/*to implement protected routes change the navitems to visibleNavItems*/}
                    {navItems.map(({to, icon: Icon, text}, index) => {
                        // Locations is restricted — only render it for authorised users.
                        // All other nav items are unaffected (global RBAC still disabled).
                        if (to === "/dashboard/locations" && !canSeeLocations) return null;
                        if (to === "/dashboard/orders" && isCarrier) return null;
                        if (to === "/dashboard/transaction-audit" && isCarrier) return null;

                        return (
                        <li key={index} data-aos="fade-right">
                            <NavLink
                                to={to}
                                className={`${navLinkStyles(to)} group relative`}
                                onClick={handleNavClick}
                            >
                                <div
                                    className={`flex items-center transition-all duration-300 w-full ${
                                        isCollapsed
                                            ? "justify-center"
                                            : "justify-start pl-[clamp(1rem, 1.5vw, 1.25rem)] pr-fluid-1 gap-fluid-1"
                                    }`}
                                >
                                    {/* Icon */}
                                    <Icon
                                        className={`w-[1em] h-[1em] transition-colors ${
                                            isActiveRoute(to) ? "text-black" : "text-white"
                                        }`}
                                    />

                                    {/* Label */}
                                    <span
                                        className={`transition-all duration-500 overflow-hidden whitespace-nowrap font-semibold ${
                                            isCollapsed ? "opacity-0 w-0" : "opacity-100 w-auto"
                                        } text-fluid-sm`}
                                    >
                    {text}
                  </span>
                                </div>

                                {/* Tooltip on hover when collapsed */}
                                {isCollapsed && (
                                    <span
                                        className="absolute left-full ml-5 top-1/2 -translate-y-1/2 z-[350]
                  bg-[#223688] text-white text-sm font-medium px-3 py-1.5
                  rounded-full shadow-xl whitespace-nowrap opacity-0 group-hover:opacity-100
                  scale-95 group-hover:scale-100 transition-all duration-300
                  pointer-events-none"
                                    >
                    {text}
                  </span>
                                )}
                            </NavLink>
                        </li>
                        );
                    })}
                </ul>
                {/* Logout Button */}
                <div className="mt-auto flex items-center w-full">
                    <NavLink
                        to="/login"
                        onClick={() => {
                            handleLogout();
                        }}
                        className={`w-full transition-all duration-300 hover:bg-red-500 hover:bg-opacity-80 py-fluid-1 rounded-lg`}
                    >
                        <div
                            className={`flex items-center transition-all duration-300 w-full ${
                                isCollapsed
                                    ? "justify-center"
                                    : "justify-start pl-[clamp(2rem,2vw+1rem,2.75rem)] pr-fluid-2 gap-fluid-1"
                            }`}
                        >
                            <LogOut className="w-[1.2em] h-[1.2em]"/>
                            <span
                                className={`ml-fluid-1 text-fluid-sm font-medium transition-all duration-300 ${
                                    isCollapsed ? "hidden" : "block"
                                }`}
                            >
                Log out
              </span>
                        </div>
                    </NavLink>
                </div>
            </nav>
        </div>
    );
};

export default Sidebar;
